import { Helmet }                                        from 'react-helmet';
import { LocationProvider, ErrorBoundary, Router, lazy } from 'preact-iso';
import { hydrate, render }                               from 'preact';
import { useEffect }                                     from 'preact/hooks';

import { routes }    from './data/routes.js';
import GlobalContext from './context/GlobalContext.js';
import useInitalData from './hooks/useInitalData.js';

import styles             from './style.css?inline';
import Loading            from './components/Loading.jsx';
import RolexAtRetailer    from './pages/RolexAtRetailer/index.jsx';
import CustomPageTemplate from './templates/CustomPageTemplate.jsx';
import schemaOrg          from './data/schemaOrg.js';

const DiscoverRolexPage = lazy(() => import('./pages/DiscoverRolex/index.jsx'))
const RolexWatchesPage = lazy(() => import('./pages/RolexWatches/index.jsx'))
const NewWatches2025Page = lazy(() => import('./pages/NewWatches2025/index.jsx'))
const WatchmakingPage = lazy(() => import('./pages/Watchmaking/index.jsx'))
const WatchPageTemplate = lazy(() => import('./templates/WatchPageTemplate.jsx'))
const WorldOfRolexArticleTemplate = lazy(() => import('./templates/WorldOfRolexArticleTemplate.jsx'))
const ServicingPage = lazy(() => import('./pages/Servicing/index.jsx'))
const ServicingProcedurePage = lazy(() => import('./pages/ServicingProcedure/index.jsx'))
const WorldOfRolexPage = lazy(() => import('./pages/WorldOfRolex/index.jsx'))
const ContactPage = lazy(() => import('./pages/Contact/index.jsx'))
const SendMessagePage = lazy(() => import('./pages/Contact/send-message.jsx'))

const Error = () => {
  useEffect(() => {
    window.location.reload();
  }, []);
  return null;
}

export function App({ apikey = 'development', pathroot = '', enableanalytics = 'true', privacylink = '/datenschutz' }) {
  const { data, isLoading, error } = useInitalData(apikey);

  const handleRouteChange = async e => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (isLoading) return <Loading/>;
  if (error) return <div>Error...</div>;

  return (
    <LocationProvider>
      <ErrorBoundary>
        <GlobalContext.Provider value={{
          pathroot: pathroot,
          retailer: data.rlx_retailer[0],
          enableanalytics: enableanalytics,
          primaryDomain: data.rlx_retailer[0].domains[0].domain,
          privacylink: privacylink,
        }}>
          <Helmet script={schemaOrg(data.rlx_retailer[0])}>
            <meta charSet="utf-8"/>
            <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
            {import.meta.env.VITE_ADOBE_ANALYTICS_ENABLED === 'true' && enableanalytics === 'true' &&
              <script src={import.meta.env.VITE_ADOBE_ANALYTICS_SCRIPT} async/>}
          </Helmet>
          <style type="text/css">{styles}</style>
          <Router onChange={handleRouteChange}>
            <DiscoverRolexPage path={pathroot + routes['discover-rolex'].de} apikey={apikey}/>
            <RolexWatchesPage path={pathroot + routes['watches'].de} apikey={apikey}/>
            <WatchPageTemplate path={pathroot + routes['watches'].de + '/:slug'} newWatches={false} apikey={apikey}/>
            <NewWatches2025Page path={pathroot + routes['new-watches-2025'].de} apikey={apikey}/>
            <WatchPageTemplate path={pathroot + routes['new-watches-2025'].de + '/:slug'} newWatches apikey={apikey}/>
            <WatchmakingPage path={pathroot + routes['watchmaking'].de} apikey={apikey}/>
            <ServicingPage path={pathroot + routes['servicing'].de} apikey={apikey}/>
            <ServicingProcedurePage path={pathroot + routes['servicing-procedure'].de} apikey={apikey}/>
            <WorldOfRolexPage path={pathroot + routes['world-of-rolex'].de} apikey={apikey}/>
            <WorldOfRolexArticleTemplate path={pathroot + routes['world-of-rolex'].de + '/:slug'} apikey={apikey}/>
            <RolexAtRetailer path={pathroot + routes['rolex-at'].de + '/:retailer'} apikey={apikey}/>
            <CustomPageTemplate path={pathroot + routes['rolex-at'].de + '/:retailer' + '/:page'} apikey={apikey}/>
            <ContactPage path={pathroot + routes['contact-us'].de} apikey={apikey}/>
            <SendMessagePage path={pathroot + routes['send-message'].de} apikey={apikey}/>
            <Error default/>
          </Router>
          <div id="popup-root"/>
        </GlobalContext.Provider>
      </ErrorBoundary>
    </LocationProvider>
  );
}


const root = document.getElementById('app');

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
  hydrate(<App/>, root);
}

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  if (root.hasChildNodes()) {
    render(<App/>, root, root.firstChild);
  } else {
    render(<App/>, root);
  }
}
